/**
 * SourceGenerator List Page
 * List and manage source generators
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  Chip,
  Link,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  GroupWork as ApplicationIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import {
  PageShell,
  DataTable,
  AlertBadge,
  generateApplicationAlerts,
  type DataTableColumn,
  DeleteWithDependenciesDialog,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { sourceGeneratorService } from '../../services/sourceGeneratorService';
import { SourceGenerator, SourceGeneratorDependencies, SourceGeneratorFilters } from '../../types/sourceGenerator';
import { dependencyItems } from '../../utils/dependencyItems';
import { SourceGeneratorStatusChip } from './components/SourceGeneratorStatusChip';
import { SourceGeneratorRiskTypeChip } from './components/SourceGeneratorRiskTypeChip';
import { SourceGeneratorFiltersComponent, SourceFiltersData } from './components/SourceGeneratorFilters';
import { useDeleteWithDependencies } from '../../hooks/useDeleteWithDependencies';

// Get alerts for a source generator
const getSourceAlerts = (source: SourceGenerator) => {
  if (!source._counts) return [];
  return generateApplicationAlerts({
    companyCount: source._counts.companies,
    departmentCount: source._counts.departments,
    occupationCount: source._counts.occupations,
  });
};

const getMaintenanceChip = (sourceGenerator: SourceGenerator): React.ReactNode => {
  const { maintenanceStatus } = sourceGenerator;

  if (!maintenanceStatus?.hasScheduledMaintenance) {
    return null;
  }

  if (maintenanceStatus.isOverdue) {
    return (
      <Tooltip title={`Manutenção atrasada (${Math.abs(maintenanceStatus.daysUntilNext ?? 0)} dias)`}>
        <Chip
          icon={<WarningIcon />}
          label="Atrasada"
          size="small"
          color="error"
          variant="outlined"
        />
      </Tooltip>
    );
  }

  if (maintenanceStatus.isDueSoon) {
    return (
      <Tooltip title={`Manutenção em ${maintenanceStatus.daysUntilNext} dias`}>
        <Chip
          icon={<BuildIcon />}
          label="Próxima"
          size="small"
          color="warning"
          variant="outlined"
        />
      </Tooltip>
    );
  }

  return null;
};

const sourceGeneratorColumns: DataTableColumn<SourceGenerator>[] = [
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (sourceGenerator) => (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" fontWeight="medium">
          {sourceGenerator.name}
        </Typography>
        <AlertBadge alerts={getSourceAlerts(sourceGenerator)} />
      </Box>
    ),
  },
  {
    id: 'location',
    label: 'Localização',
    render: (sourceGenerator) => (
      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
        {sourceGenerator.location}
      </Typography>
    ),
  },
  {
    id: 'riskType',
    label: 'Tipo de Risco',
    render: (sourceGenerator) => <SourceGeneratorRiskTypeChip type={sourceGenerator.riskType} />,
  },
  {
    id: 'maintenance',
    label: 'Manutenção',
    render: (sourceGenerator) =>
      getMaintenanceChip(sourceGenerator) || (
        <Typography variant="caption" color="text.secondary">
          -
        </Typography>
      ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (sourceGenerator) => <SourceGeneratorStatusChip status={sourceGenerator.status} />,
  },
];

export const SourceGeneratorListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/source-generators/:id/edit', pode('source_generator.update'), 'Editar');
  const excluir = useAcao('source_generator.delete', 'Excluir');
  const [sourceGenerators, setSourceGenerators] = useState<SourceGenerator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<SourceGeneratorFilters>({});

  const loadSourceGenerators = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
      };

      const response = await sourceGeneratorService.getSourceGenerators(params);
      setSourceGenerators(response.sourceGenerators);
      setTotal(response.total);
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message ?? 'Erro ao carregar fontes geradoras');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadSourceGenerators();
  }, [loadSourceGenerators]);

  const handleFilter = useCallback((data: SourceFiltersData) => {
    const filters: SourceGeneratorFilters = {};

    if (data.searchTerm) filters.search = data.searchTerm;
    if (data.riskType) filters.riskType = data.riskType as SourceGeneratorFilters['riskType'];
    if (data.status) filters.status = data.status as SourceGeneratorFilters['status'];
    if (data.maintenanceOverdue) filters.maintenanceOverdue = true;

    setApiFilters(filters);
    setPage(0);
  }, []);

  const { deleteTarget, deleteDependencies, handleDeleteClick, closeDeleteDialog, handleDeleteConfirm } =
    useDeleteWithDependencies<SourceGenerator, SourceGeneratorDependencies>({
      checkDependencies: (id) => sourceGeneratorService.checkDependencies(id),
      deleteEntity: (id) => sourceGeneratorService.deleteSourceGenerator(id),
      checkErrorMessage: 'Erro ao verificar vínculos da fonte geradora',
      deleteErrorMessage: 'Erro ao excluir fonte geradora',
      onError: setError,
      onDeleted: loadSourceGenerators,
    });

  const handleEdit = (sourceGenerator: SourceGenerator) => {
    navigate(`/source-generators/${sourceGenerator.id}/edit`);
  };

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Fontes Geradoras' }]}
      title="Gestão de Fontes Geradoras"
      subtitle="Gerencie as fontes geradoras de riscos ocupacionais"
      actions={
        <>
          <RouteActionButton
            para="/source-applications"
            exige={pode('source_application.read', { perspectiva: 'plataforma' })}
            variant="outlined"
            color="secondary"
            startIcon={<ApplicationIcon />}
          >
            Aplicar Fontes
          </RouteActionButton>
          <RouteActionButton
            para="/source-generators/new"
            exige={pode('source_generator.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Nova Fonte
          </RouteActionButton>
        </>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Máquinas físicas de uma empresa se cadastram em{' '}
        <Link component={RouterLink} to="/machines">
          Máquinas
        </Link>
        .
      </Alert>

      <Box sx={{ mb: 3 }}>
        <SourceGeneratorFiltersComponent
          onFilter={handleFilter}
          loading={loading}
          total={total}
        />
      </Box>

      <DataTable
          columns={sourceGeneratorColumns}
          rows={sourceGenerators}
          getRowId={(sourceGenerator) => sourceGenerator.id}
          loading={loading}
          emptyState={{
            title: 'Nenhuma fonte geradora encontrada',
            description:
              'Fontes geradoras são registradas nesta tela e depois vinculadas aos riscos ocupacionais que elas originam.',
          }}
          actions={[
            {
              icon: <EditIcon fontSize="small" />,
              onClick: handleEdit,
              ...editar,
            },
            {
              icon: <DeleteIcon fontSize="small" />,
              color: 'error',
              onClick: handleDeleteClick,
              ...excluir,
            },
          ]}
          pagination={{
            page,
            rowsPerPage,
            totalCount: total,
            onPageChange: setPage,
            onRowsPerPageChange: (value) => {
              setRowsPerPage(value);
              setPage(0);
            },
          }}
      />

      <DeleteWithDependenciesDialog
        open={Boolean(deleteTarget)}
        loading={deleteDependencies === null}
        blocked={Boolean(deleteDependencies?.hasDependencies)}
        title="Excluir fonte geradora"
        confirmText="Tem certeza que deseja excluir esta fonte geradora?"
        prerequisite={{
          title: 'Esta fonte geradora está em uso',
          reason: 'Fontes geradoras com aplicações ativas não podem ser excluídas — remova as aplicações primeiro.',
          items: dependencyItems(deleteDependencies?.details),
          resolve: {
            label: 'Ver aplicações',
            permission: 'source_generator.update',
            mode: 'navigate',
            route: `/source-applications?sourceId=${deleteTarget?.id}`,
          },
          deniedHint: 'Peça a um administrador para remover as aplicações desta fonte geradora antes de excluí-la.',
        }}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />
    </PageShell>
  );
};

export default SourceGeneratorListPage;
